import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, styled } from '@mui/material';

import { formatAbTestValue } from './formatAbTestValue';
import DataOverviewCard from 'src/components/reusable-components/data-overview-card';
import DataOverviewCardSkeleton from 'src/components/reusable-components/data-overview-card/DataOverviewCardSkeleton';
import {
  useGetAbTestSummaryChartsQuery,
  useListAbTestConfigsSummaryQuery,
} from 'src/services/agent-ab-test/agentAbTestApi';
import { useFetchDatasetQuery } from 'src/services/insight-cards';
import {
  CustomEventChart,
  DatasetRequest,
  SeriesItem,
} from 'src/services/insight-cards/types';

function buildDatasetParams(
  chart: CustomEventChart,
  endTimestamp: number,
): DatasetRequest {
  return {
    aggregation_type: chart.aggregation_type,
    channels: chart.channels,
    end_timestamp: endTimestamp,
    formula_text: chart.formula_text,
    group_by: chart.group_by,
    measurement_type: chart.measurement_type,
    properties: chart.properties,
    start_timestamp: 0,
  };
}

function getSeriesValue(
  dataset: ReturnType<typeof useFetchDatasetQuery>['data'],
): number {
  if (!dataset) {
    return 0;
  }
  const series: SeriesItem[] = dataset.is_grouped
    ? (dataset.series[0] ?? [])
    : dataset.series;
  return series[0]?.data ?? 0;
}

function SummaryChartCard({
  chart,
  endTimestamp,
}: {
  chart: CustomEventChart;
  endTimestamp: number;
}) {
  const { data: dataset } = useFetchDatasetQuery(
    buildDatasetParams(chart, endTimestamp),
  );

  const valueSuffix = dataset?.tooltip?.value_suffix;
  const value = getSeriesValue(dataset);

  return (
    <DataOverviewCard
      colorMode='dark'
      label={chart.name}
      value={formatAbTestValue(value, chart.aggregation_type, valueSuffix)}
    />
  );
}

function DeflectionsSummaryCard({
  countChart,
  endTimestamp,
  rateChart,
}: {
  countChart: CustomEventChart;
  endTimestamp: number;
  rateChart: CustomEventChart;
}) {
  const { data: countDataset } = useFetchDatasetQuery(
    buildDatasetParams(countChart, endTimestamp),
  );

  const { data: rateDataset } = useFetchDatasetQuery(
    buildDatasetParams(rateChart, endTimestamp),
  );

  const countValue = getSeriesValue(countDataset);
  const rateValue = getSeriesValue(rateDataset);
  const rateSuffix = rateDataset?.tooltip?.value_suffix;

  const formattedCount = formatAbTestValue(
    countValue,
    countChart.aggregation_type,
  );
  const formattedRate = formatAbTestValue(
    rateValue,
    rateChart.aggregation_type,
    rateSuffix,
  );

  return (
    <DataOverviewCard
      colorMode='dark'
      label={countChart.name}
      value={`${formattedCount} (${formattedRate})`}
    />
  );
}

const AbTestSummaryCards = () => {
  const { t } = useTranslation('agent-ab-test');
  const { data: charts, isLoading: isLoadingCharts } =
    useGetAbTestSummaryChartsQuery();
  const { data: configsData } = useListAbTestConfigsSummaryQuery({
    page: 1,
    pageSize: 5000,
  });

  // End of today in seconds — recomputes naturally on re-render (e.g. after midnight)
  const endTimestamp = Math.floor(new Date().setHours(23, 59, 59, 0) / 1000);
  const totalTests = configsData?.ab_test_configs?.length ?? 0;

  const chartsByKey = useMemo(() => {
    if (!charts) {
      return null;
    }
    const map = new Map(charts.map(c => [c.chart_id, c]));
    const conversations = map.get('ab_test_summary_total_conversations');
    const deflectionsCount = map.get('ab_test_summary_deflections_count');
    const deflectionRate = map.get('ab_test_summary_deflection_rate');
    const csat = map.get('ab_test_summary_csat');
    if (!conversations || !deflectionsCount || !deflectionRate || !csat) {
      return null;
    }
    return { conversations, csat, deflectionRate, deflectionsCount };
  }, [charts]);

  if (isLoadingCharts || !chartsByKey) {
    return (
      <SummaryCardsGrid>
        {Array.from({ length: 4 }).map((_, i) => (
          <DataOverviewCardSkeleton key={i} />
        ))}
      </SummaryCardsGrid>
    );
  }

  return (
    <SummaryCardsGrid>
      <DataOverviewCard
        colorMode='dark'
        label={t('summaryCards.totalTests')}
        value={String(totalTests)}
      />
      <SummaryChartCard
        chart={chartsByKey.conversations}
        endTimestamp={endTimestamp}
      />
      <DeflectionsSummaryCard
        countChart={chartsByKey.deflectionsCount}
        endTimestamp={endTimestamp}
        rateChart={chartsByKey.deflectionRate}
      />
      <SummaryChartCard chart={chartsByKey.csat} endTimestamp={endTimestamp} />
    </SummaryCardsGrid>
  );
};

const SummaryCardsGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 22px;
`;

export default AbTestSummaryCards;
